import { useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Stack, FormControlLabel, Checkbox, Select, MenuItem, FormControl, InputLabel,
  Typography, CircularProgress, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import authFetch from '../utils/authFetch';
import { NAVY, inputSx, btn } from '../styles/ds';

/**
 * Return goods from a delivered order item: quantity, restock toggle
 * (unchecked for damaged goods), and an optional refund that is recorded
 * as a credit ('refunded' payment) on the order.
 */
const ReturnDialog = ({ open, onClose, item, onSaved }) => {
  const notify = useNotify();
  const [quantity, setQuantity]   = useState(1);
  const [restock, setRestock]     = useState(true);
  const [refund, setRefund]       = useState('');
  const [currency, setCurrency]   = useState('ILS');
  const [notes, setNotes]         = useState('');
  const [saving, setSaving]       = useState(false);

  if (!item) return null;

  const handleSave = async () => {
    if (!(Number(quantity) > 0)) { notify('נא להזין כמות', { type: 'warning' }); return; }
    setSaving(true);
    try {
      const res = await authFetch('/returns', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_item_id: item.id,
          quantity: Number(quantity),
          restock,
          refund_amount: refund ? Number(refund) : null,
          refund_currency: currency,
          notes: notes || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'שגיאה בביצוע ההחזרה');
      notify('ההחזרה נרשמה' + (restock ? ' והמלאי עודכן' : ''), { type: 'success' });
      onSaved?.();
      onClose();
    } catch (e) {
      notify(e.message, { type: 'error' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} dir="rtl" maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: NAVY }}>
        החזרת פריט
        <IconButton size="small" onClick={onClose} aria-label="סגור"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography fontSize={14} fontWeight={600} mb={2}>{item.label}</Typography>
        <Stack spacing={2}>
          <TextField
            label={`כמות להחזרה (נמכרו ${item.quantity})`} type="number" size="small"
            value={quantity} onChange={e => setQuantity(e.target.value)}
            inputProps={{ min: 1, max: item.quantity }} sx={inputSx}
          />
          <FormControlLabel
            control={<Checkbox checked={restock} onChange={e => setRestock(e.target.checked)} />}
            label="החזרת הפריט למלאי (בטלי סימון אם הפריט פגום)"
          />
          <Stack className="crm-filter-row" direction="row" spacing={1.5}>
            <TextField
              label="זיכוי כספי (אופציונלי)" type="number" size="small" fullWidth
              value={refund} onChange={e => setRefund(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }} sx={inputSx}
            />
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>מטבע</InputLabel>
              <Select value={currency} label="מטבע" onChange={e => setCurrency(e.target.value)} sx={inputSx}>
                <MenuItem value="ILS">₪ שקל</MenuItem>
                <MenuItem value="USD">$ דולר</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <TextField label="סיבת ההחזרה" size="small" multiline rows={2}
            value={notes} onChange={e => setNotes(e.target.value)} sx={inputSx} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: '#666' }}>ביטול</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <KeyboardReturnIcon />}
          sx={btn.primary}>
          בצע החזרה
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReturnDialog;
